import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import BrandMark from '../components/BrandMark';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import useAuth from '../hooks/useAuth';
import { logout } from '../store/slices/authSlice';
import { getInitials } from '../utils/helpers';
import FontSizeControl from '../components/FontSizeControl';
import { BRAND, OCEAN, keyframe, reduceMotion } from '../theme/oceanTokens';

/* Footer links: a short aqua rail slides in on hover, matching the hero. */
const footerLinkSx = {
  display: 'block',
  position: 'relative',
  width: 'fit-content',
  color: 'rgba(255,255,255,0.68)',
  textDecoration: 'none',
  py: 0.6,
  fontSize: '0.9rem',
  transition: 'color .2s ease, padding-left .25s cubic-bezier(.2,.8,.3,1)',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: '50%',
    width: 10,
    height: 2,
    borderRadius: 99,
    background: `linear-gradient(90deg, ${OCEAN.sky}, ${OCEAN.aqua})`,
    transform: 'translateY(-50%) scaleX(0)',
    transformOrigin: 'left',
    transition: 'transform .25s cubic-bezier(.2,.8,.3,1)',
  },
  '&:hover': { color: OCEAN.aqua, pl: 2 },
  '&:hover::before': { transform: 'translateY(-50%) scaleX(1)' },
  [reduceMotion]: { transition: 'none', '&::before': { transition: 'none' } },
};

const footerHeadingSx = {
  fontWeight: 700,
  color: '#fff',
  mb: 2,
  letterSpacing: '0.5px',
  position: 'relative',
  width: 'fit-content',
  pb: 0.8,
  '&::after': {
    content: '""',
    position: 'absolute',
    left: 0,
    bottom: 0,
    width: 28,
    height: 3,
    borderRadius: 99,
    background: `linear-gradient(90deg, ${OCEAN.ocean}, ${OCEAN.aqua})`,
  },
};

const footerContactSx = {
  color: 'rgba(255,255,255,0.68)',
  textDecoration: 'none',
  fontSize: '0.9rem',
  transition: 'color .2s ease',
  '&:hover': { color: OCEAN.aqua },
};

const NAV_LINKS = [
  { label: 'Home', path: '/' },
  { label: 'Doctors', path: '/doctors' },
];

const PublicLayout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const hideFooter = ['/login', '/register'].includes(location.pathname);
  const dispatch = useDispatch();
  const { user, isAuthenticated, role } = useAuth();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const getDashboardPath = () => {
    if (role === 'admin') return '/admin/dashboard';
    if (role === 'hospital_admin') return '/hospital/dashboard';
    if (role === 'lab_staff') return '/lab';
    return '/customer/dashboard';
  };

  const handleLogout = () => {
    handleMenuClose();
    dispatch(logout());
    navigate('/login');
  };

  const mobileDrawer = (
    <Box sx={{ width: 280, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BrandMark size={30} />
          <Typography variant="h6" fontWeight={700} color="primary.main">
            PyMedic
          </Typography>
        </Box>
        <IconButton onClick={handleDrawerToggle} size="small">
          <CloseIcon />
        </IconButton>
      </Box>
      <List sx={{ flex: 1, py: 2 }}>
        {NAV_LINKS.map((link) => (
          <ListItemButton
            key={link.path}
            selected={location.pathname === link.path}
            onClick={() => {
              navigate(link.path);
              handleDrawerToggle();
            }}
            sx={{ mx: 1.5, borderRadius: 2 }}
          >
            <ListItemText
              primary={link.label}
              primaryTypographyProps={{ fontWeight: 600 }}
            />
          </ListItemButton>
        ))}
      </List>
      <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'center' }}>
        <FontSizeControl />
      </Box>
      <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        {isAuthenticated ? (
          <>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                navigate(getDashboardPath());
                handleDrawerToggle();
              }}
              sx={{ mb: 1, borderRadius: 2 }}
            >
              Dashboard
            </Button>
            <Button
              fullWidth
              variant="contained"
              color="error"
              onClick={() => {
                handleLogout();
                handleDrawerToggle();
              }}
              sx={{ borderRadius: 2 }}
            >
              Logout
            </Button>
          </>
        ) : (
          <>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                navigate('/login');
                handleDrawerToggle();
              }}
              sx={{ mb: 1, borderRadius: 2 }}
            >
              Login
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                navigate('/register');
                handleDrawerToggle();
              }}
              sx={{ borderRadius: 2 }}
            >
              Register
            </Button>
          </>
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="sticky" sx={{ zIndex: theme.zIndex.drawer + 1 }}>
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ minHeight: { xs: 64, md: 72 } }}>
            {isMobile && (
              <IconButton
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 1 }}
              >
                <MenuIcon />
              </IconButton>
            )}

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
                mr: 4,
              }}
              onClick={() => navigate('/')}
            >
              <BrandMark size={38} />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  letterSpacing: '-0.5px',
                }}
              >
                PyMedic
              </Typography>
            </Box>

            {!isMobile && (
              <Box sx={{ display: 'flex', gap: 0.5, flex: 1 }}>
                {NAV_LINKS.map((link) => (
                  <Button
                    key={link.path}
                    component={Link}
                    to={link.path}
                    sx={{
                      fontWeight: 600,
                      color:
                        location.pathname === link.path
                          ? 'primary.main'
                          : 'text.secondary',
                      bgcolor:
                        location.pathname === link.path
                          ? alpha(theme.palette.primary.main, 0.08)
                          : 'transparent',
                      borderRadius: 2,
                      px: 2,
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.06),
                      },
                    }}
                  >
                    {link.label}
                  </Button>
                ))}
              </Box>
            )}

            <Box sx={{ flex: 1 }} />

            {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <FontSizeControl compact />
                {isAuthenticated ? (
                  <>
                    <IconButton onClick={handleMenuOpen} sx={{ p: 0.5 }}>
                      <Avatar
                        src={user?.profile_photo}
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor: 'primary.main',
                          fontSize: '0.9rem',
                          fontWeight: 700,
                          border: '2px solid',
                          borderColor: alpha(theme.palette.primary.main, 0.2),
                        }}
                      >
                        {getInitials(user?.first_name || user?.name || 'U')}
                      </Avatar>
                    </IconButton>
                    <Menu
                      anchorEl={anchorEl}
                      open={menuOpen}
                      onClose={handleMenuClose}
                      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                      PaperProps={{
                        sx: {
                          mt: 1,
                          borderRadius: 3,
                          minWidth: 200,
                          border: '1px solid',
                          borderColor: 'divider',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                        },
                      }}
                    >
                      <Box sx={{ px: 2.5, py: 1.5 }}>
                        <Typography variant="subtitle1" fontWeight={600} color="text.primary">
                          {user?.first_name} {user?.last_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {user?.email}
                        </Typography>
                      </Box>
                      <Divider sx={{ my: 0.5 }} />
                      <MenuItem
                        onClick={() => {
                          handleMenuClose();
                          navigate(getDashboardPath());
                        }}
                        sx={{ py: 1.2, px: 2.5 }}
                      >
                        <ListItemIcon>
                          <DashboardIcon fontSize="small" />
                        </ListItemIcon>
                        Dashboard
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          handleMenuClose();
                          navigate(
                            role === 'customer'
                              ? '/customer/profile'
                              : getDashboardPath()
                          );
                        }}
                        sx={{ py: 1.2, px: 2.5 }}
                      >
                        <ListItemIcon>
                          <PersonIcon fontSize="small" />
                        </ListItemIcon>
                        Profile
                      </MenuItem>
                      <Divider sx={{ my: 0.5 }} />
                      <MenuItem
                        onClick={handleLogout}
                        sx={{ py: 1.2, px: 2.5, color: 'error.main' }}
                      >
                        <ListItemIcon>
                          <LogoutIcon fontSize="small" sx={{ color: 'error.main' }} />
                        </ListItemIcon>
                        Logout
                      </MenuItem>
                    </Menu>
                  </>
                ) : (
                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Button
                      variant="outlined"
                      onClick={() => navigate('/login')}
                      sx={{ borderRadius: 2.5, px: 3 }}
                    >
                      Login
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => navigate('/register')}
                      sx={{ borderRadius: 2.5, px: 3 }}
                    >
                      Register
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: 280,
            borderRadius: '0 16px 16px 0',
          },
        }}
      >
        {mobileDrawer}
      </Drawer>

      <Box component="main" sx={{ flex: 1 }}>
        <Outlet />
      </Box>

      {/* Sign-in and sign-up are single-purpose screens: the footer's four
          columns of links only give someone a way to wander off mid-login. */}
      {!hideFooter && (
      <Box
        component="footer"
        sx={{
          mt: 'auto',
          position: 'relative',
          overflow: 'hidden',
          color: 'rgba(255,255,255,0.85)',
          background: `linear-gradient(165deg, ${OCEAN.ink} 0%, #0A2F42 55%, ${BRAND.c1} 100%)`,
          // Gradient rule along the top edge, the same stops as the hero.
          borderTop: '3px solid transparent',
          borderImage: `linear-gradient(90deg, ${BRAND.c1}, ${OCEAN.ocean}, ${OCEAN.aqua}) 1`,
        }}
      >
        {/* Drifting glows, as on the hero and the login panel. */}
        <Box
          sx={{
            position: 'absolute',
            width: 360,
            height: 360,
            top: -180,
            right: '8%',
            borderRadius: '50%',
            background: OCEAN.ocean,
            filter: 'blur(90px)',
            opacity: 0.35,
            pointerEvents: 'none',
            animation: `${keyframe.drift2} 24s ease-in-out infinite`,
            [reduceMotion]: { animation: 'none' },
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: 300,
            height: 300,
            bottom: -200,
            left: -60,
            borderRadius: '50%',
            background: OCEAN.aqua,
            filter: 'blur(90px)',
            opacity: 0.22,
            pointerEvents: 'none',
            animation: `${keyframe.drift1} 20s ease-in-out infinite`,
            [reduceMotion]: { animation: 'none' },
          }}
        />
        {/* Main footer content */}
        <Container
          maxWidth="lg"
          sx={{ position: 'relative', pt: { xs: 5, md: 7 }, pb: { xs: 4, md: 5 } }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: '1fr 1fr',
                md: '2fr 1fr 1fr 1fr',
              },
              gap: { xs: 4, md: 5 },
            }}
          >
            {/* Column 1: Brand */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 2 }}>
                {/* The brand mark, glowing on the dark footer. */}
                <BrandMark
                  size={44}
                  glow
                  sx={{
                    animation: `${keyframe.float} 5s ease-in-out infinite`,
                    [reduceMotion]: { animation: 'none' },
                  }}
                />
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 800,
                    letterSpacing: '-0.5px',
                    background: `linear-gradient(90deg, #fff, ${OCEAN.aqua}, ${OCEAN.sky}, #fff)`,
                    backgroundSize: '240% 100%',
                    WebkitBackgroundClip: 'text',
                    backgroundClip: 'text',
                    color: 'transparent',
                    animation: `${keyframe.hue} 8s ease-in-out infinite`,
                    [reduceMotion]: { animation: 'none' },
                  }}
                >
                  PyMedic
                </Typography>
              </Box>
              <Typography
                variant="body1"
                sx={{ color: 'rgba(255,255,255,0.78)', lineHeight: 1.7, mb: 2, maxWidth: 300 }}
              >
                Your trusted healthcare appointment platform. Connecting patients with the right doctors, effortlessly.
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255,255,255,0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  fontStyle: 'italic',
                }}
              >
                Made with{' '}
                <FavoriteIcon
                  sx={{
                    fontSize: 14,
                    color: '#FB7185',
                    animation: `${keyframe.blink} 2.2s ease-in-out infinite`,
                    [reduceMotion]: { animation: 'none' },
                  }}
                />{' '}
                for better healthcare
              </Typography>
            </Box>

            {/* Column 2: Quick Links */}
            <Box>
              <Typography variant="subtitle1" sx={footerHeadingSx}>
                Quick Links
              </Typography>
              {[
                { label: 'Home', to: '/' },
                { label: 'Find Doctors', to: '/doctors' },
                { label: 'Login', to: '/login' },
                { label: 'Register', to: '/register' },
              ].map((item) => (
                <Typography key={item.to} component={Link} to={item.to} sx={footerLinkSx}>
                  {item.label}
                </Typography>
              ))}
            </Box>

            {/* Column 3: For Hospitals */}
            <Box>
              <Typography variant="subtitle1" sx={footerHeadingSx}>
                For Hospitals
              </Typography>
              {[
                { label: 'Register Hospital', to: '/hospital/register' },
                { label: 'Hospital Login', to: '/login' },
                { label: 'Manage Appointments', to: '/hospital/dashboard' },
              ].map((item) => (
                <Typography key={item.label} component={Link} to={item.to} sx={footerLinkSx}>
                  {item.label}
                </Typography>
              ))}
            </Box>

            {/* Column 4: Support */}
            <Box>
              <Typography variant="subtitle1" sx={footerHeadingSx}>
                Support
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {[
                  { icon: EmailOutlinedIcon, text: 'help@pymedic.com', href: 'mailto:help@pymedic.com' },
                  { icon: PhoneOutlinedIcon, text: '+91 1800-XXX-XXXX', href: 'tel:+911800XXXXXXX' },
                  { icon: AccessTimeIcon, text: 'Mon–Sat, 9AM–6PM IST' },
                ].map(({ icon: Icon, text, href }) => (
                  <Box
                    key={text}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.2,
                      '&:hover .footer-glyph': {
                        background: `linear-gradient(135deg, ${OCEAN.ocean}, ${OCEAN.aqua})`,
                        color: '#fff',
                        transform: 'scale(1.08)',
                      },
                    }}
                  >
                    <Box
                      className="footer-glyph"
                      sx={{
                        width: 30,
                        height: 30,
                        flexShrink: 0,
                        display: 'grid',
                        placeItems: 'center',
                        borderRadius: '9px',
                        color: OCEAN.aqua,
                        bgcolor: 'rgba(255,255,255,0.08)',
                        border: '1px solid rgba(255,255,255,0.12)',
                        transition: 'background .25s, color .2s, transform .25s',
                        [reduceMotion]: { transition: 'none' },
                      }}
                    >
                      <Icon sx={{ fontSize: 16 }} />
                    </Box>
                    {href ? (
                      <Typography component="a" href={href} sx={footerContactSx}>
                        {text}
                      </Typography>
                    ) : (
                      <Typography sx={footerContactSx}>{text}</Typography>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Container>

        {/* Bottom bar */}
        <Box
          sx={{
            position: 'relative',
            borderTop: '1px solid rgba(255,255,255,0.1)',
            bgcolor: 'rgba(0,0,0,0.18)',
          }}
        >
          <Container
            maxWidth="lg"
            sx={{
              py: 2.5,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
            }}
          >
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.85rem' }}>
              &copy; 2026 PyMedic. All rights reserved.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2.5 }}>
              {['Terms', 'Privacy', 'Contact'].map((item) => (
                <Typography
                  key={item}
                  component={Link}
                  to={`/${item.toLowerCase()}`}
                  sx={{
                    color: 'rgba(255,255,255,0.5)',
                    textDecoration: 'none',
                    fontSize: '0.85rem',
                    transition: 'color 0.2s ease',
                    '&:hover': { color: OCEAN.aqua },
                  }}
                >
                  {item}
                </Typography>
              ))}
            </Box>
          </Container>
        </Box>
      </Box>
      )}
    </Box>
  );
};

export default PublicLayout;
